<template>
  <div class="bvp">
    <div v-if="showChannelSelect || showLayoutSwitch || showRefresh" class="bvp__bar">
      <template v-if="showChannelSelect">
        <span class="bvp__label">通道</span>
        <el-select v-model="activeStream" size="mini" style="width:220px" @change="onSelectChange">
          <el-option v-for="ch in channels" :key="ch.streamId"
                     :label="chLabel(ch)" :value="ch.streamId" :disabled="!ch.pullable" />
        </el-select>
      </template>
      <el-radio-group v-if="showLayoutSwitch" v-model="layout" size="mini" @change="onLayoutChange">
        <el-radio-button label="1x1">单画面</el-radio-button>
        <el-radio-button label="2x2">四分屏</el-radio-button>
        <el-radio-button label="3x3">九分屏</el-radio-button>
      </el-radio-group>
      <el-button v-if="showRefresh" size="mini" icon="el-icon-refresh" @click="reload">刷新</el-button>
    </div>

    <div class="bvp__body" :class="{ 'is-rev': channelListRight }">
      <div v-if="showChannelList" class="bvp__side">
        <div class="bvp__side-head">{{ sideTitle || '视频通道' }}</div>
        <div class="bvp__side-list">
          <div v-for="ch in channels" :key="ch.streamId" class="bvp__side-item"
               :class="{ 'is-active': activeStream === ch.streamId, 'is-off': !ch.pullable }"
               @click="selectChannel(ch)">
            <i class="el-icon-video-camera"></i>
            <span class="bvp__side-name">NVR#{{ ch.nvrId }} · CH{{ ch.channel }}</span>
            <span class="bvp__side-code">{{ ch.codec || '?' }}</span>
          </div>
        </div>
        <div class="bvp__side-tip">共 {{ channels.length }} 路 · 可拉流 {{ pullableCount }} 路</div>
      </div>

      <div class="bvp__grid" :class="'is-' + layout">
        <div v-for="ch in visibleChannels" :key="ch.streamId + '-' + reloadKey" class="bvp__cell"
             :class="{ 'is-active': activeStream === ch.streamId }" @click="selectChannel(ch)">
          <div class="bvp__cell-head">
            <span class="bvp__cell-name">NVR#{{ ch.nvrId }} · {{ ch.name || ('CH' + ch.channel) }}</span>
            <span class="bvp__cell-code">{{ ch.codec }} {{ ch.res }}</span>
          </div>
          <div class="bvp__cell-body">
            <nvr-player v-if="ch.wsUrl" :url="ch.wsUrl"></nvr-player>
            <div v-else class="bvp__cell-hint" :class="{ 'is-err': !!ch.err }">
              {{ ch.loading ? '正在拉起流…' : (ch.err || (ch.pullable ? '待拉流' : '该 NVR 未开放 RTSP 端口，无法播放')) }}
            </div>
          </div>
        </div>
        <div v-if="!visibleChannels.length" class="bvp__empty">{{ emptyText }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import NvrPlayer from './NvrPlayer.vue'
import { getChannels, ensureStream, releaseStream } from '@/api/video'

export default {
  name: 'BridgeVideoPanel',
  components: { NvrPlayer },
  props: {
    bridgeId: { type: [String, Number], default: '' },
    defaultLayout: { type: String, default: '2x2' },
    showChannelList: { type: Boolean, default: false },
    channelListRight: { type: Boolean, default: false },
    showChannelSelect: { type: Boolean, default: false },
    showLayoutSwitch: { type: Boolean, default: true },
    showRefresh: { type: Boolean, default: true },
    sideTitle: { type: String, default: '' },
    emptyText: { type: String, default: '未选择桥梁，或该桥梁没有可视通道' }
  },
  data() {
    return {
      layout: this.defaultLayout,
      channels: [],
      activeStream: '',
      reloadKey: 0,
      held: []
    }
  },
  computed: {
    pullableCount() {
      return this.channels.filter(c => c.pullable).length
    },
    visibleChannels() {
      if (this.layout === '1x1') {
        return this.channels.filter(c => c.streamId === this.activeStream)
      }
      const limit = this.layout === '3x3' ? 9 : 4
      return this.channels.slice(0, limit)
    },
    activeChannel() {
      return this.channels.find(c => c.streamId === this.activeStream) || null
    }
  },
  watch: {
    bridgeId() {
      this.loadChannels()
    },
    activeChannel: {
      immediate: true,
      handler(ch) {
        this.$emit('active-change', ch)
      }
    }
  },
  created() {
    this.loadChannels()
    window.addEventListener('beforeunload', this.releaseAll)
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.releaseAll)
    this.releaseAll()
  },
  methods: {
    chLabel(ch) {
      return 'NVR#' + ch.nvrId + ' · CH' + ch.channel + (ch.pullable ? '' : '（未开放）')
    },
    keyOf(ch) {
      return ch.nvrId + '-' + ch.channel
    },
    async loadChannels() {
      this.releaseAll()
      this.channels = []
      this.activeStream = ''
      if (!this.bridgeId) {
        this.$emit('channels-change', [])
        return
      }
      try {
        const res = await getChannels({ bridgeId: this.bridgeId })
        const list = (res && res.data) || []
        this.channels = list.map(c => ({
          ...c, wsUrl: null, loading: false, err: ''
        }))
        const first = this.channels.find(c => c.pullable) || this.channels[0]
        if (first) this.activeStream = first.streamId
      } catch (e) {
        this.channels = []
      }
      this.$emit('channels-change', this.channels)
      this.$nextTick(this.prepareVisible)
    },
    onSelectChange() {
      if (this.layout === '1x1') this.$nextTick(this.prepareVisible)
    },
    onLayoutChange() {
      this.$emit('layout-change', this.layout)
      if (this.layout === '1x1' && !this.activeChannel) {
        const first = this.channels.find(c => c.pullable)
        if (first) this.activeStream = first.streamId
      }
      this.$nextTick(this.prepareVisible)
    },
    selectChannel(ch) {
      if (!ch) return
      this.activeStream = ch.streamId
      if (this.layout === '1x1') this.$nextTick(this.prepareVisible)
    },
    // 供外部（弹窗右侧通道列表）切换通道
    selectStream(streamId) {
      if (!streamId) return
      this.activeStream = streamId
      if (this.layout === '1x1') this.$nextTick(this.prepareVisible)
    },
    async prepareVisible() {
      const visKeys = new Set(this.visibleChannels.filter(c => c.pullable).map(c => this.keyOf(c)))
      const keep = []
      const drop = []
      this.held.forEach(h => (visKeys.has(h.key) ? keep : drop).push(h))
      this.held = keep
      drop.forEach(h => releaseStream(h.nvrId, h.channel).catch(() => {}))
      const targets = this.visibleChannels.filter(c => c.pullable && !c.wsUrl && !c.loading)
      await Promise.all(targets.map(c => this.ensureChannel(c)))
    },
    async ensureChannel(ch) {
      ch.loading = true
      ch.err = ''
      try {
        const res = await ensureStream(ch.nvrId, ch.channel)
        if (res && res.code === 200 && res.data && res.data.wsUrl) {
          ch.wsUrl = res.data.wsUrl
          const key = this.keyOf(ch)
          if (!this.held.some(h => h.key === key)) {
            this.held.push({ nvrId: ch.nvrId, channel: ch.channel, key })
          }
        } else {
          ch.err = (res && res.message) || '拉流失败'
        }
      } catch (e) {
        ch.err = '拉流异常：' + e.message
      }
      ch.loading = false
    },
    releaseAll() {
      const items = this.held.slice()
      this.held = []
      items.forEach(h => releaseStream(h.nvrId, h.channel).catch(() => {}))
    },
    reload() {
      this.reloadKey++
      this.releaseAll()
      this.channels.forEach(c => { c.wsUrl = null; c.err = '' })
      this.$nextTick(this.prepareVisible)
    }
  }
}
</script>

<style scoped>
.bvp { height: 100%; display: flex; flex-direction: column; background: #0f172a; min-height: 0; }
.bvp__bar {
  flex: 0 0 auto; display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 8px 10px; background: #1e293b; color: #e2e8f0; border-bottom: 1px solid #334155;
}
.bvp__label { font-size: 12px; color: #94a3b8; }
.bvp__tip { font-size: 12px; color: #64748b; margin-left: auto; }
.bvp__body { flex: 1; display: flex; min-height: 0; }
.bvp__body.is-rev { flex-direction: row-reverse; }
.bvp__body.is-rev .bvp__side { border-right: none; border-left: 1px solid #334155; }
.bvp__side {
  width: 210px; flex: 0 0 210px; background: #1e293b; color: #cbd5e1;
  display: flex; flex-direction: column; overflow: hidden;
  padding: 8px; border-right: 1px solid #334155;
}
.bvp__side-list { flex: 1; min-height: 0; overflow-y: auto; }
.bvp__side-head { flex: 0 0 auto; font-size: 12px; color: #94a3b8; padding: 6px 8px 10px; border-bottom: 1px solid #334155; margin-bottom: 6px; }
.bvp__side-item { display: flex; align-items: center; gap: 8px; padding: 9px 8px; border-radius: 6px; cursor: pointer; font-size: 13px; transition: all .2s; }
.bvp__side-item:hover { background: #334155; color: #fff; }
.bvp__side-item.is-active { background: #2563eb; color: #fff; }
.bvp__side-item.is-off { opacity: .5; }
.bvp__side-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bvp__side-code { font-size: 11px; color: #94a3b8; }
.bvp__side-tip { flex: 0 0 auto; font-size: 12px; color: #64748b; padding: 8px 8px 4px; border-top: 1px solid #334155; margin-top: 6px; }
.bvp__grid { flex: 1; min-width: 0; display: grid; gap: 8px; padding: 8px; min-height: 0; position: relative; }
.bvp__grid.is-2x2 { grid-template-columns: repeat(2, 1fr); grid-template-rows: repeat(2, 1fr); }
.bvp__grid.is-3x3 { grid-template-columns: repeat(3, 1fr); grid-template-rows: repeat(3, 1fr); }
.bvp__grid.is-1x1 { grid-template-columns: 1fr; grid-template-rows: 1fr; }
.bvp__cell { display: flex; flex-direction: column; min-height: 0; background: #000; border: 1px solid #334155; border-radius: 6px; overflow: hidden; cursor: pointer; transition: border-color .2s; }
.bvp__cell.is-active { border-color: #38bdf8; box-shadow: 0 0 0 1px #38bdf8 inset; }
.bvp__cell-head { flex: 0 0 26px; display: flex; align-items: center; justify-content: space-between; padding: 0 8px; font-size: 12px; color: #cbd5e1; background: #111827; }
.bvp__cell-code { color: #64748b; }
.bvp__cell-body { flex: 1; min-height: 0; position: relative; }
.bvp__cell-hint {
  position: absolute; top: 0; right: 0; bottom: 0; left: 0; display: flex; align-items: center;
  justify-content: center; color: #94a3b8; font-size: 12px; padding: 8px; text-align: center; background: #000;
}
.bvp__cell-hint.is-err { color: #fca5a5; }
.bvp__empty {
  position: absolute; top: 0; right: 0; bottom: 0; left: 0; display: flex; align-items: center;
  justify-content: center; color: #94a3b8; font-size: 13px; text-align: center; padding: 16px;
}

/* 顶栏控件深色化，与整体深色主题保持一致 */
.bvp__bar ::v-deep .el-radio-button__inner {
  background: #334155; color: #cbd5e1; border-color: #475569; box-shadow: none;
}
.bvp__bar ::v-deep .el-radio-button:first-child .el-radio-button__inner { border-left-color: #475569; }
.bvp__bar ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background: #2563eb; border-color: #2563eb; color: #fff; box-shadow: none;
}
.bvp__bar ::v-deep .el-button {
  background: #334155; color: #cbd5e1; border-color: #475569;
}
.bvp__bar ::v-deep .el-button:not(.is-disabled):hover,
.bvp__bar ::v-deep .el-button:not(.is-disabled):focus {
  background: #3f4d63; color: #fff; border-color: #64748b;
}
.bvp__bar ::v-deep .el-input__inner {
  background: #334155; color: #e2e8f0; border-color: #475569;
}
.bvp__bar ::v-deep .el-input__inner::placeholder { color: #94a3b8; }
</style>
